<template>
  <v-form ref="form" v-if="siteData">
    <h2 class="text-subtitle-2 font-weight-medium my-3">Site Geofencing</h2>
    <div v-if="showGeocodeStatusMessage" class="mb-2 text-caption">
      <span v-if="displayGeocodeStatus === 'pending'" class="font-weight-medium">Geocoding…</span>
      <template v-else-if="displayGeocodeStatus === 'failed'">
        <span class="font-weight-medium text-error">Geocoding failed.</span>
        <span class="text-error"> Retry or set location manually.</span>
      </template>
    </div>
    <v-btn
      v-if="effectiveCoordinates"
      variant="tonal"
      color="secondary"
      @click="generateAddressCoordinates"
      :loading="isCoordinatesPending"
      :disabled="!isAddressValid() || !canTriggerGeocode"
      class="mb-2 text-capitalize"
      style="font-size: 14px"
    >
      <template #prepend>
        <v-icon left class="hidden-sm-and-down" color="primary"> mdi-crosshairs-gps </v-icon>
      </template>
      Regenerate Coordinates
    </v-btn>
    <div style="border: 1px solid #767676; border-radius: 8px">
      <v-row no-gutters class="ma-0" align="stretch">
        <v-col cols="9" class="d-flex">
          <div style="min-height: 380px; height: 100%; width: 100%">
            <MapboxMap
              v-if="effectiveCoordinates && mapDeferredReady && !mapLoadError"
              ref="mapRef"
              :access-token="accessToken"
              map-style="mapbox://styles/mapbox/satellite-streets-v12"
              :center="mapCenter"
              :zoom="DEFAULT_MAP_ZOOM"
              style="height: 100%; width: 100%; border-radius: 8px 0px 0px 8px"
              @mb-created="onMapCreated"
            >
            </MapboxMap>

            <div
              v-else-if="mapLoadError"
              class="d-flex flex-column align-center justify-center pa-4 text-center"
              style="height: 100%; min-height: 380px; background: #f5f5f5; border-radius: 8px 0 0 8px"
            >
              <span class="text-body-2 text-medium-emphasis mb-2">Map unavailable</span>
              <span class="text-caption">
                WebGL could not be initialized in this browser. You can still set location and geofences; try enabling
                hardware acceleration in Chrome or use another browser.
              </span>
            </div>

            <NoDataAvailablePlaceholder
              v-else
              width="400"
              header="No Geofence available"
              :description="noLocationMessage"
              header-class="font-weight-medium text-h5"
            >
              <template #description>
                <p class="mt-0 mb-6 text-caption text-medium-emphasis">
                  {{ noLocationMessage }}
                </p>
              </template>

              <template #primaryAction>
                <v-btn
                  color="primary"
                  depressed
                  @click="generateAddressCoordinates"
                  :loading="isCoordinatesPending"
                  :disabled="!canTriggerGeocode || !isAddressValid()"
                >
                  <template #prepend>
                    <v-icon left class="hidden-sm-and-down"> mdi-crosshairs-gps </v-icon>
                  </template>
                  Generate Coordinates
                </v-btn>
              </template>
            </NoDataAvailablePlaceholder>
          </div>
        </v-col>

        <v-col cols="3" v-if="siteData.geofences && siteData.geofences[0]">
          <div style="background-color: #f1f1f1; border-radius: 0px 8px 0px 0px" class="pa-4">
            <div class="pb-1" style="font-size: 12px; font-weight: bold">
              Select Geofencing Area
            </div>
            <div style="font-size: 12px">Choose the shape of the geofence area</div>
          </div>

          <v-radio-group
            color="primary"
            class="pt-4 pl-1"
            v-model="siteData.geofences[0].shape"
            hide-details="auto"
            :disabled="!isAddressValid() || !effectiveCoordinates"
          >
            <v-radio label="Round" :value="GeofenceShapeType.Round"></v-radio>
            <v-radio label="Rectangular" :value="GeofenceShapeType.Rectangular"></v-radio>
            <v-radio label="None" :value="null"></v-radio>
          </v-radio-group>

          <div
            class="px-4 pb-4 pt-3"
            v-if="siteData.geofences[0].shape !== null && isAddressValid() && effectiveCoordinates"
          >
            <div class="pb-3" style="font-size: 12px; font-weight: 500">
              Please enter shape size
            </div>
            <template v-if="siteData.geofences[0].shape === GeofenceShapeType.Round">
              <v-text-field
                v-model.number="siteData.geofences[0].radius"
                label="Radius (meters)*"
                variant="outlined"
                hide-details="auto"
                density="compact"
                type="number"
                :rules="[requiredValidator, positiveNumberValidator]"
                placeholder="Enter radius in meters"
                @update:model-value="updateMapFromFields"
              ></v-text-field>
            </template>

            <template v-if="siteData.geofences[0].shape === GeofenceShapeType.Rectangular">
              <v-text-field
                v-model.number="siteData.geofences[0].width"
                label="Width (meters)*"
                variant="outlined"
                hide-details="auto"
                density="compact"
                type="number"
                :rules="[requiredValidator, positiveNumberValidator]"
                placeholder="Enter width in meters"
                @update:model-value="updateMapFromFields"
              ></v-text-field>

              <v-text-field
                class="mt-4"
                v-model.number="siteData.geofences[0].height"
                label="Height (meters)*"
                variant="outlined"
                hide-details="auto"
                density="compact"
                type="number"
                :rules="[requiredValidator, positiveNumberValidator]"
                placeholder="Enter height in meters"
                @update:model-value="updateMapFromFields"
              ></v-text-field>
            </template>
          </div>

          <div v-if="canManageGeofences" class="px-4 pb-4">
            <v-btn
              v-if="siteData.geofences[0].shape !== null || selectedGeofenceId != null"
              color="primary"
              size="small"
              variant="tonal"
              class="mb-1"
              :loading="isCreateGeofencePending || isUpdateGeofencePending || isDeleteGeofencePending"
              :disabled="siteData.geofences[0].shape !== null && !canSaveGeofence"
              @click="saveCurrentGeofence"
            >
              {{
                siteData.geofences[0].shape === null && hasExistingGeofence
                  ? 'Remove geofence'
                  : hasExistingGeofence
                    ? 'Save geofence'
                    : 'Add geofence'
              }}
            </v-btn>
            <p class="text-caption text-medium-emphasis mb-2 mt-0">
              Saves this geofence to the site. Use «Save site» at the bottom to save site name and address.
            </p>
          </div>
        </v-col>
      </v-row>
    </div>
  </v-form>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick, onUnmounted, onMounted, onErrorCaptured, unref } from 'vue'
import mapboxgl from 'mapbox-gl'
import { MapboxMap } from '@studiometa/vue-mapbox-gl'

import type { ICoordinates, ICoordinatesData, ISiteFormData } from '@/models/client'
import { requiredValidator, positiveNumberValidator } from '@/utils/validators'
import {
  createCircleGeoJSON,
  createMarkerElement,
  createRectangleGeoJSON,
  rectangleToPolygonCoordinates,
  polygonCoordinatesToRectangle
} from '@/utils/geofence'
import { GeofenceShapeType } from '@/models/client/site.constants'
import { Address } from '@/models/address'
import { locationToLatLng } from '@/models/client'
import { useSiteGeocodeWithPolling, useSetSiteLocation } from '@/composables/site'
import { useQueryClient } from '@tanstack/vue-query'
import {
  useGeofences,
  useCreateGeofence,
  useUpdateGeofence,
  useDeleteGeofence
} from '@/composables/geofencing'
import type { IGeofenceData, IGeofenceCircleData, IGeofencePolygonData } from '@/models/geofencing'
import { createGeofence as toGeofenceModel } from '@/models/geofencing'

import NoDataAvailablePlaceholder from '@/components/common/NoDataAvailablePlaceholder.vue'

const props = withDefaults(
  defineProps<{
    clientId?: number
    siteCode?: number
    /** When in edit mode, called before triggering geocode so the parent can persist address first. */
    onBeforeGeocode?: () => Promise<void>
  }>(),
  { clientId: undefined, siteCode: undefined, onBeforeGeocode: undefined }
)

// Constants
const DEFAULT_DIMENSION_METERS = 100
const MAP_UPDATE_DELAY_MS = 100
const MAP_PADDING_PX = 50
const COORDINATE_DECIMAL_PRECISION = 7
const DEFAULT_MAP_ZOOM = 14
const GEOFENCE_LINE_WIDTH = 2
const GEOFENCE_FILL_OPACITY = 0.3

const accessToken = import.meta.env.VITE_APP_MAP_BOX_TOKEN

const siteData = defineModel<ISiteFormData>({ required: true })

// Ensure a geofence object exists on the site model so template bindings work
if (!siteData.value.geofences || siteData.value.geofences.length === 0) {
  siteData.value.geofences = [{ shape: null }]
}

// Real coordinates from API or manually set (excludes fallback)
const realCoordinates = computed(() => {
  const fromLocation = locationToLatLng(siteData.value.location)
  if (fromLocation?.latitude != null && fromLocation?.longitude != null) return fromLocation
  const c = siteData.value.coordinates
  if (c?.latitude != null && c?.longitude != null) return c
  return null
})

// Effective coordinates for map: real coordinates or fallback for display
const effectiveCoordinates = computed(() => {
  const real = realCoordinates.value
  if (real) return real
  
  // Fallback: Show map at Orangeville, ON when geocoding fails so user can manually set location
  if (siteData.value.geocode_status === 'failed') {
    return { latitude: 43.9201, longitude: -80.0978 }
  }
  
  return null
})

// Defer map mount until after dialog is visible so WebGL can initialize (avoids "Failed to initialize WebGL" in Chrome when canvas is in transitioning dialog)
const mapDeferredReady = ref(false)
const mapLoadError = ref(false)
let mapDeferTimer: ReturnType<typeof setTimeout> | null = null
watch(
  effectiveCoordinates,
  (coords) => {
    if (mapDeferTimer) {
      clearTimeout(mapDeferTimer)
      mapDeferTimer = null
    }
    mapLoadError.value = false
    if (coords) {
      // Double rAF + delay so Chrome has time to lay out the dialog before WebGL init
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          mapDeferTimer = setTimeout(() => {
            if (effectiveCoordinates.value) mapDeferredReady.value = true
            mapDeferTimer = null
          }, 400)
        })
      })
    } else {
      mapDeferredReady.value = false
    }
  },
  { immediate: true }
)
onUnmounted(() => {
  if (mapDeferTimer) clearTimeout(mapDeferTimer)
})
onErrorCaptured((err: unknown) => {
  const msg = err instanceof Error ? err.message : String(err)
  if (msg.includes('WebGL') || msg.includes('Failed to initialize')) {
    mapLoadError.value = true
    return false
  }
})

const canTriggerGeocode = computed(() => !!props.siteCode && !!props.clientId)
const noLocationMessage = computed(() =>
  canTriggerGeocode.value
    ? 'No coordinates yet. Add address and click Generate Coordinates, or save the site first.'
    : 'Save the site first, then edit to set location and geofence.'
)

const clientIdRef = computed(() => props.clientId ?? 0)
const siteCodeRef = computed(() => props.siteCode ?? 0)
const { triggerAndPoll, isPolling, geocodeStatus, isPending: isTriggerPending } =
  useSiteGeocodeWithPolling(clientIdRef, siteCodeRef)
const { setLocation: setLocationApi, isPending: isSetLocationPending } = useSetSiteLocation(
  clientIdRef,
  siteCodeRef
)

const isCoordinatesPending = computed(
  () => isTriggerPending.value || isPolling.value || isSetLocationPending.value
)

const displayGeocodeStatus = computed(
  () => geocodeStatus.value ?? siteData.value.geocode_status
)
// Only show status when user needs to act or wait (pending/failed); hide when ok or not_set
const showGeocodeStatusMessage = computed(
  () => displayGeocodeStatus.value === 'pending' || displayGeocodeStatus.value === 'failed'
)

const canManageGeofences = computed(() => !!props.siteCode)
const queryClient = useQueryClient()
const { geofences: geofencesList } = useGeofences(
  computed(() => props.siteCode)
)
const hasExistingGeofence = computed(() => (unref(geofencesList)?.length ?? 0) > 0)
const { createGeofence, isPending: isCreateGeofencePending } = useCreateGeofence(
  computed(() => props.siteCode ?? 0)
)
const { updateGeofence, isPending: isUpdateGeofencePending } = useUpdateGeofence(
  computed(() => props.siteCode ?? 0)
)
const { deleteGeofence: deleteGeofenceApi, isPending: isDeleteGeofencePending } =
  useDeleteGeofence(computed(() => props.siteCode ?? 0))

const selectedGeofenceId = ref<number | null>(null)

// Frontend supports single geofence only: when list has one, load it into the form; when none, clear form only if not drafting (avoid wiping unsaved shape/dimensions on refetch)
watch(
  () => unref(geofencesList) ?? [],
  (list) => {
    const g = siteData.value.geofences?.[0]
    if (!g) return
    if (!Array.isArray(list) || list.length === 0) {
      // Only clear when form is not an in-progress draft (user selected shape but hasn't clicked Add geofence)
      if (g.shape === null) {
        g.radius = null
        g.width = null
        g.height = null
        g.center_coordinates = null
        selectedGeofenceId.value = null
        nextTick(() => updateMapVisualization())
      }
      return
    }
    if (list.length >= 1 && g.shape === null) {
      const gf = list[0]
      selectedGeofenceId.value = gf.id ?? null
      loadGeofenceIntoForm(gf)
    }
  },
  { immediate: true }
)

function buildGeofencePayload(): IGeofenceCircleData | IGeofencePolygonData | null {
  const center = getGeofenceCenter()
  if (!center) return null
  const g = siteData.value.geofences?.[0]
  if (!g) return null
  if (g.shape === GeofenceShapeType.Round && g.radius != null && g.radius > 0) {
    return {
      shape_type: 'circle',
      circle: { lat: center.latitude, lng: center.longitude, radius_m: Math.round(g.radius) }
    }
  }
  if (
    g.shape === GeofenceShapeType.Rectangular &&
    g.width != null &&
    g.height != null &&
    g.width > 0 &&
    g.height > 0
  ) {
    const coordinates = rectangleToPolygonCoordinates(
      center.longitude,
      center.latitude,
      g.width,
      g.height
    )
    return { shape_type: 'polygon', polygon: { coordinates } }
  }
  return null
}

const canSaveGeofence = computed(() => !!buildGeofencePayload())

function loadGeofenceIntoForm(gf: IGeofenceData) {
  const g = siteData.value.geofences![0]
  if (gf.shape_type === 'circle' && gf.circle) {
    g.shape = GeofenceShapeType.Round
    g.center_coordinates = { latitude: gf.circle.lat, longitude: gf.circle.lng }
    g.radius = gf.circle.radius_m
    g.width = null
    g.height = null
  } else if (gf.shape_type === 'polygon' && gf.polygon) {
    const rect = polygonCoordinatesToRectangle(gf.polygon.coordinates)
    if (rect) {
      g.shape = GeofenceShapeType.Rectangular
      g.center_coordinates = { latitude: rect.centerLat, longitude: rect.centerLng }
      g.width = Math.round(rect.widthMeters)
      g.height = Math.round(rect.heightMeters)
      g.radius = null
    }
  }
  nextTick(() => updateMapVisualization())
}

async function saveCurrentGeofence() {
  const g = siteData.value.geofences?.[0]
  if (!g || !props.siteCode) return

  // None + existing geofence = remove it
  if (g.shape === null && selectedGeofenceId.value != null) {
    try {
      await deleteGeofenceApi(selectedGeofenceId.value)
      selectedGeofenceId.value = null
      g.radius = null
      g.width = null
      g.height = null
      g.center_coordinates = null
      await queryClient.refetchQueries({ queryKey: ['geofences', props.siteCode] })
      nextTick(() => updateMapVisualization())
    } catch (e) {
      console.error('Remove geofence failed:', e)
    }
    return
  }

  const payload = buildGeofencePayload()
  if (!payload) return
  try {
    if (selectedGeofenceId.value != null) {
      await updateGeofence({ id: selectedGeofenceId.value, payload })
      // Keep selectedGeofenceId so form stays in edit mode for the single geofence
    } else {
      await createGeofence(payload)
      siteData.value.geofences![0].shape = null
      siteData.value.geofences![0].radius = null
      siteData.value.geofences![0].width = null
      siteData.value.geofences![0].height = null
      siteData.value.geofences![0].center_coordinates = null
    }
    await queryClient.refetchQueries({ queryKey: ['geofences', props.siteCode] })
    nextTick(() => updateMapVisualization())
  } catch (e) {
    console.error('Save geofence failed:', e)
  }
}

// Helper: validate address before geocoding
function isAddressValid(): boolean {
  if (!siteData.value.address) return false

  try {
    new Address(siteData.value.address)
    return true
  } catch {
    return false
  }
}

let map: mapboxgl.Map | null = null
let centerMarker: mapboxgl.Marker | null = null
let siteCoordinatesMarker: mapboxgl.Marker | null = null
let isUpdatingFromMap = false
let isDraggingGeofence = false
let hasBoundGeofenceDragHandlers = false
let dragStartLngLat: { lng: number; lat: number } | null = null
let originalCenter: ICoordinates | null = null

// Map center used by MapboxMap (GeoJSON order: lng, lat)
const mapCenter = computed<[number, number]>(() => {
  const c = effectiveCoordinates.value
  return c ? [Number(c.longitude), Number(c.latitude)] : [0, 0]
})

function centerMapOnCoordinates(coordinates: ICoordinatesData) {
  if (!map) return
  const center: [number, number] = [
    Number(coordinates.longitude),
    Number(coordinates.latitude)
  ]
  map.flyTo({ center, zoom: DEFAULT_MAP_ZOOM })
}

// Resolve geofence center (prefer geofence center, fallback to real site location only)
function getGeofenceCenter(): ICoordinates | null {
  const centerCoordinates = siteData.value.geofences?.[0]?.center_coordinates
  // Use only real coordinates, never the Orangeville fallback
  const siteCoords = realCoordinates.value

  const source =
    centerCoordinates?.latitude != null && centerCoordinates?.longitude != null
      ? centerCoordinates
      : siteCoords

  if (source?.latitude == null || source?.longitude == null) return null

  return {
    longitude: Number(source.longitude),
    latitude: Number(source.latitude)
  }
}

// Ensure the red site marker exists and stays in sync with site location/coordinates
function syncSiteCoordinatesMarker() {
  if (!map) return
  const coords = effectiveCoordinates.value
  if (coords?.latitude == null || coords?.longitude == null) return

  if (siteCoordinatesMarker) {
    siteCoordinatesMarker.setLngLat([
      Number(coords.longitude),
      Number(coords.latitude)
    ])
    return
  }

  siteCoordinatesMarker = new mapboxgl.Marker({ draggable: true, color: '#FF0000' })
    .setLngLat([Number(coords.longitude), Number(coords.latitude)])
    .addTo(map)

  siteCoordinatesMarker.on('dragend', async () => {
    const lngLat = siteCoordinatesMarker!.getLngLat()
    const lat = Number(lngLat.lat.toFixed(COORDINATE_DECIMAL_PRECISION))
    const lng = Number(lngLat.lng.toFixed(COORDINATE_DECIMAL_PRECISION))
    const previousCoords = effectiveCoordinates.value
    isUpdatingFromMap = true

    if (canTriggerGeocode.value) {
      try {
        const updated = await setLocationApi({ lat, lng })
        siteData.value.location = updated.location ?? { type: 'Point', coordinates: [lng, lat] }
        siteData.value.geocode_status = updated.geocode_status
        siteData.value.location_source = updated.location_source
        if (!siteData.value.geofences?.[0]?.center_coordinates) {
          siteData.value.geofences![0].center_coordinates = { latitude: lat, longitude: lng }
        }
      } catch {
        // Revert marker to previous position so UI matches backend (location is not persisted on Save)
        if (previousCoords?.latitude != null && previousCoords?.longitude != null) {
          siteCoordinatesMarker!.setLngLat([
            Number(previousCoords.longitude),
            Number(previousCoords.latitude)
          ])
        }
      }
    } else {
      siteData.value.coordinates = { latitude: lat, longitude: lng }
      if (!siteData.value.geofences?.[0]?.center_coordinates) {
        siteData.value.geofences![0].center_coordinates = { latitude: lat, longitude: lng }
      }
    }
    setTimeout(() => {
      isUpdatingFromMap = false
    }, MAP_UPDATE_DELAY_MS)
  })
}

// Ensure the blue geofence center marker exists and stays in sync
function syncGeofenceCenterMarker(geofenceCenter: ICoordinates) {
  if (!map) return

  if (siteData.value.geofences![0].shape) {
    if (centerMarker) {
      centerMarker.setLngLat([geofenceCenter.longitude, geofenceCenter.latitude])
      return
    }

    const geofenceMarkerEl = createMarkerElement('geofence-center', '#0088FF')
    centerMarker = new mapboxgl.Marker({ draggable: true, element: geofenceMarkerEl })
      .setLngLat([geofenceCenter.longitude, geofenceCenter.latitude])
      .addTo(map)

    centerMarker.on('dragend', () => {
      const lngLat = centerMarker!.getLngLat()
      isUpdatingFromMap = true
      siteData.value.geofences![0].center_coordinates = {
        longitude: Number(lngLat.lng.toFixed(COORDINATE_DECIMAL_PRECISION)),
        latitude: Number(lngLat.lat.toFixed(COORDINATE_DECIMAL_PRECISION))
      }
      setTimeout(() => {
        isUpdatingFromMap = false
        updateMapVisualization()
      }, MAP_UPDATE_DELAY_MS)
    })
    return
  }

  if (centerMarker) {
    centerMarker.remove()
    centerMarker = null
  }
}

// Build the geofence polygon based on shape and dimensions
function buildGeofenceGeoJson(
  geofenceCenter: ICoordinates,
  shape: GeofenceShapeType | null | undefined,
  radius?: number | null,
  width?: number | null,
  height?: number | null
): GeoJSON.Feature<GeoJSON.Polygon> | null {
  if (shape === GeofenceShapeType.Round && radius) {
    return createCircleGeoJSON(geofenceCenter.longitude, geofenceCenter.latitude, Number(radius))
  }
  if (shape === GeofenceShapeType.Rectangular && width && height) {
    return createRectangleGeoJSON(
      geofenceCenter.longitude,
      geofenceCenter.latitude,
      Number(width),
      Number(height)
    )
  }
  return null
}

function buildFeatureCollectionFromList(
  list: IGeofenceData[]
): GeoJSON.FeatureCollection<GeoJSON.Polygon> {
  const features: GeoJSON.Feature<GeoJSON.Polygon>[] = list
    .map((data) => {
      try {
        const gf = toGeofenceModel(data)
        if (gf.shape_type === 'circle') {
          return createCircleGeoJSON(
            gf.circle.lng,
            gf.circle.lat,
            gf.circle.radius_m
          )
        }
        if (gf.shape_type === 'polygon') {
          const coords = gf.polygon.coordinates
          const ring: [number, number][] = coords.length >= 4 ? [...coords] : [...coords]
          if (ring.length >= 4 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
            ring.push([ring[0][0], ring[0][1]])
          }
          return {
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: [ring] },
            properties: {}
          }
        }
      } catch (e) {
        console.error('Invalid geofence data:', data, e)
        return null
      }
      return null
    })
    .filter((f): f is GeoJSON.Feature<GeoJSON.Polygon> => f !== null)
  return { type: 'FeatureCollection', features }
}

// Enable dragging the geofence shape to update center coordinates
function onGeofenceMouseDown(e: mapboxgl.MapMouseEvent) {
  if (!map) return
  const features = map.queryRenderedFeatures(e.point, { layers: ['geofence-fill'] })
  if (features.length === 0) return
  const geofenceCenter = getGeofenceCenter()
  if (!geofenceCenter) return
  e.preventDefault()
  isDraggingGeofence = true
  dragStartLngLat = e.lngLat
  originalCenter = {
    longitude: geofenceCenter.longitude,
    latitude: geofenceCenter.latitude
  }
  map.getCanvas().style.cursor = 'move'
}

function onGeofenceMouseMove(e: mapboxgl.MapMouseEvent) {
  if (!isDraggingGeofence || !dragStartLngLat || !originalCenter || !map) return

  const deltaLng = e.lngLat.lng - dragStartLngLat.lng
  const deltaLat = e.lngLat.lat - dragStartLngLat.lat

  const newCenter = {
    longitude: Number((originalCenter.longitude + deltaLng).toFixed(COORDINATE_DECIMAL_PRECISION)),
    latitude: Number((originalCenter.latitude + deltaLat).toFixed(COORDINATE_DECIMAL_PRECISION))
  }

  isUpdatingFromMap = true
  siteData.value.geofences![0].center_coordinates = newCenter
  setTimeout(() => {
    isUpdatingFromMap = false
  }, MAP_UPDATE_DELAY_MS)
}

function onGeofenceMouseUp() {
  if (!map) return
  isDraggingGeofence = false
  dragStartLngLat = null
  originalCenter = null
  map.getCanvas().style.cursor = ''
  // Redraw polygon/markers at new center after drag; run after isUpdatingFromMap is reset from last mousemove
  setTimeout(() => {
    updateMapVisualization()
  }, MAP_UPDATE_DELAY_MS)
}

function bindGeofenceDragHandlers() {
  if (!map || hasBoundGeofenceDragHandlers) return
  map.on('mousedown', onGeofenceMouseDown)
  map.on('mousemove', onGeofenceMouseMove)
  map.on('mouseup', onGeofenceMouseUp)
  hasBoundGeofenceDragHandlers = true
}

// Fit map view to include the geofence (and site marker if present)
function fitBoundsToGeofence(geojson: GeoJSON.Feature<GeoJSON.Polygon>) {
  if (!map) return
  const bounds = new mapboxgl.LngLatBounds()
  ;(geojson.geometry.coordinates[0] as [number, number][]).forEach((coord) => {
    bounds.extend(coord)
  })

  const coords = effectiveCoordinates.value
  if (coords?.latitude != null && coords?.longitude != null) {
    bounds.extend([Number(coords.longitude), Number(coords.latitude)])
  }

  map.fitBounds(bounds, { padding: MAP_PADDING_PX })
}

// Fit map view to a FeatureCollection (saved geofences)
function fitBoundsToFeatureCollection(collection: GeoJSON.FeatureCollection<GeoJSON.Polygon>) {
  if (!map || !collection.features?.length) return
  const bounds = new mapboxgl.LngLatBounds()
  collection.features.forEach((f) => {
    if (f.geometry?.type === 'Polygon' && f.geometry.coordinates?.[0]) {
      f.geometry.coordinates[0].forEach((c) => bounds.extend(c as [number, number]))
    }
  })
  const coords = effectiveCoordinates.value
  if (coords?.latitude != null && coords?.longitude != null) {
    bounds.extend([Number(coords.longitude), Number(coords.latitude)])
  }
  if (bounds.isEmpty()) return
  map.fitBounds(bounds, { padding: MAP_PADDING_PX })
}

// Primary map render pipeline: draw form geofence when editing/adding, or saved geofences from list when form is None
function updateMapVisualization() {
  if (!map || isUpdatingFromMap || !map.isStyleLoaded()) return

  const { shape, radius, width, height } = siteData.value.geofences![0]
  const geofenceCenter = getGeofenceCenter()
  const list = unref(geofencesList) ?? []

  syncSiteCoordinatesMarker()
  removeMapLayers()

  if (shape && geofenceCenter) {
    syncGeofenceCenterMarker(geofenceCenter)
    const geojson = buildGeofenceGeoJson(geofenceCenter, shape, radius, width, height)
    if (geojson) {
      map.addSource('geofence', {
        type: 'geojson',
        data: geojson
      })
      map.addLayer({
        id: 'geofence-fill',
        type: 'fill',
        source: 'geofence',
        paint: {
          'fill-color': '#088',
          'fill-opacity': GEOFENCE_FILL_OPACITY
        }
      })
      map.addLayer({
        id: 'geofence-outline',
        type: 'line',
        source: 'geofence',
        paint: {
          'line-color': '#088',
          'line-width': GEOFENCE_LINE_WIDTH
        }
      })
      bindGeofenceDragHandlers()
      fitBoundsToGeofence(geojson)
    }
    return
  }

  if (Array.isArray(list) && list.length > 0) {
    syncGeofenceCenterMarker(geofenceCenter ?? { latitude: 0, longitude: 0 })
    const collection = buildFeatureCollectionFromList(list)
    map.addSource('geofence', {
      type: 'geojson',
      data: collection
    })
    map.addLayer({
      id: 'geofence-fill',
      type: 'fill',
      source: 'geofence',
      paint: {
        'fill-color': '#088',
        'fill-opacity': GEOFENCE_FILL_OPACITY
      }
    })
    map.addLayer({
      id: 'geofence-outline',
      type: 'line',
      source: 'geofence',
      paint: {
        'line-color': '#088',
        'line-width': GEOFENCE_LINE_WIDTH
      }
    })
    fitBoundsToFeatureCollection(collection)
    return
  }

  syncGeofenceCenterMarker(geofenceCenter ?? { latitude: 0, longitude: 0 })
}

// Update map when fields change
function updateMapFromFields() {
  if (isUpdatingFromMap) return
  nextTick(() => {
    updateMapVisualization()
  })
}

// Handle map creation
function onMapCreated(mapInstance: mapboxgl.Map) {
  map = mapInstance

  // Wait for map style to load before initializing visualization
  if (map.isStyleLoaded()) {
    nextTick(() => {
      updateMapVisualization()
    })
  } else {
    map.once('load', () => {
      nextTick(() => {
        updateMapVisualization()
      })
    })
  }

  bindGeofenceDragHandlers()
}

// Clear shape-specific fields when shape changes; only set default dimensions when current ones are empty (preserve loaded geofence dimensions when entering edit)
watch(
  () => siteData.value.geofences?.[0].shape,
  async (newShape) => {
    removeMapLayers()
    const g = siteData.value.geofences![0]
    if (newShape === null) {
      g.radius = null
      g.width = null
      g.height = null
      g.center_coordinates = null
    } else if (newShape === GeofenceShapeType.Rectangular) {
      g.radius = null
      if (g.width == null) g.width = DEFAULT_DIMENSION_METERS
      if (g.height == null) g.height = DEFAULT_DIMENSION_METERS
    } else if (newShape === GeofenceShapeType.Round) {
      g.width = null
      g.height = null
      if (g.radius == null) g.radius = DEFAULT_DIMENSION_METERS
    }
    updateMapFromFields()
  }
)

function removeMapLayers() {
  if (map) {
    if (map.getLayer('geofence-center')) map.removeLayer('geofence-center')
    if (map.getLayer('geofence-fill')) map.removeLayer('geofence-fill')
    if (map.getLayer('geofence-outline')) map.removeLayer('geofence-outline')
    if (map.getSource('geofence')) map.removeSource('geofence')
  }
}

// Cleanup on unmount
onUnmounted(() => {
  if (centerMarker) {
    centerMarker.remove()
  }
  if (siteCoordinatesMarker) {
    siteCoordinatesMarker.remove()
  }
  if (map && hasBoundGeofenceDragHandlers) {
    map.off('mousedown', onGeofenceMouseDown)
    map.off('mousemove', onGeofenceMouseMove)
    map.off('mouseup', onGeofenceMouseUp)
    hasBoundGeofenceDragHandlers = false
  }
})

// Trigger geocode (async): persist address first if parent provided onBeforeGeocode (edit mode), then POST .../geocode/ and poll
async function generateAddressCoordinates() {
  if (!canTriggerGeocode.value || !isAddressValid()) return
  try {
    if (props.onBeforeGeocode) await props.onBeforeGeocode()
  } catch (e) {
    console.error('Failed to persist site before geocode:', e)
    return
  }
  const force = siteData.value.location_source === 'manual'
  try {
    const updated = await triggerAndPoll(force)
    if (updated) {
      siteData.value.location = updated.location ?? null
      siteData.value.geocode_status = updated.geocode_status
      siteData.value.location_source = updated.location_source
      siteData.value.geocoded_at = updated.geocoded_at
      const coords = locationToLatLng(updated.location)
      if (coords && (!siteData.value.geofences?.length || !siteData.value.geofences[0].center_coordinates)) {
        if (!siteData.value.geofences?.length) {
          siteData.value.geofences = [{ shape: null, center_coordinates: coords }]
        } else {
          siteData.value.geofences[0].center_coordinates = coords
        }
      }
      await nextTick()
      if (coords) centerMapOnCoordinates(coords)
      updateMapVisualization()
    }
  } catch (e) {
    console.error('Failed to trigger geocode:', e)
  }
}

onMounted(() => {
  if (effectiveCoordinates.value) {
    updateMapVisualization()
  }
})
</script>

<style scoped>
:deep(.mapboxgl-canvas) {
  cursor: crosshair;
}
</style>
